/* lab-article-audio.js: "Listen to this article" player
   One <audio preload="none"> per block. No libraries, no build step.
   Adds .is-playing / .is-loading / .has-error to the .ar-audio block and
   writes progress to the --ar-audio-p custom property.
   Controls: play/pause, seek (click/drag/keyboard), back 15s, forward 15s,
   speed 1x / 1.25x / 1.5x / 2x. Media Session metadata + action handlers so
   phone lock screens, AirPods and car head units drive it. */
(function () {
  var RATES = [1, 1.25, 1.5, 2];
  var BARS = 40;

  function fmt(s) {
    if (!isFinite(s) || s < 0) s = 0;
    s = Math.round(s);
    var m = Math.floor(s / 60);
    return m + ':' + String(s % 60).padStart(2, '0');
  }

  function init(root) {
    if (root.dataset.arAudioReady) return;
    root.dataset.arAudioReady = '1';

    var audio = root.querySelector('.ar-audio__native');
    var play = root.querySelector('.ar-audio__play');
    var back = root.querySelector('[data-ar-back]');
    var fwd = root.querySelector('[data-ar-fwd]');
    var seek = root.querySelector('.ar-audio__seek');
    var time = root.querySelector('.ar-audio__time');
    var rate = root.querySelector('.ar-audio__rate');
    var err = root.querySelector('.ar-audio__error');
    if (!audio || !play) return;

    var BACK_BY = parseInt(root.dataset.arBack || '15', 10);
    var FWD_BY = parseInt(root.dataset.arFwd || '15', 10);
    var rateIdx = 0, dragging = false, loaded = false;

    /* ---- position memory ----------------------------------------------
       Two different resets to defend against. To a listener who pressed pause
       and came back, both look like "it started over":
         1. The browser throws the media buffer away while the element is
            paused (iOS does this aggressively to reclaim memory) and reloads
            from byte 0 on the next play. `preload="none"` makes it likelier.
         2. The page is reloaded or revisited, so the element is brand new.
       So the position is held BOTH in a variable for this page view and in
       localStorage keyed by the file, and put back whenever the element turns
       up rewound. Everything is in try/catch: Safari private mode throws on
       localStorage, and a dead player is worse than a forgetful one. */
    var KEY = 'le-audio:' + (audio.getAttribute('src') || '').split('?')[0];
    var MAX_AGE = 30 * 24 * 3600 * 1000;   /* a month, then it is a new listen */
    var MIN_MARK = 5;                      /* under 5s, starting over is no loss */
    var mark = 0, lastWrite = 0;

    function remember(t) {
      mark = t > 0 ? t : 0;
      if (Date.now() - lastWrite < 4000) return;   /* localStorage is sync; do not write on every timeupdate */
      lastWrite = Date.now();
      try {
        if (mark > MIN_MARK) localStorage.setItem(KEY, JSON.stringify({ t: mark, at: Date.now() }));
        else localStorage.removeItem(KEY);
      } catch (e) {}
    }
    function forget() {
      mark = 0; lastWrite = 0;
      try { localStorage.removeItem(KEY); } catch (e) {}
    }
    /* currentTime cannot be set before the browser knows the media, so this
       runs on metadata/emptied rather than at init. */
    function restore() {
      if (!mark || audio.currentTime > 1) return;
      var d = dur();
      if (d && mark >= d - 1) { forget(); return; }   /* finished last time: start over */
      try { audio.currentTime = mark; } catch (e) {}
      paint();
    }
    /* What the listener thinks the position is, which is the mark until the
       element has actually loaded and caught up. */
    function at() {
      return audio.currentTime > 0 ? audio.currentTime : mark;
    }
    try {
      var prev = JSON.parse(localStorage.getItem(KEY) || 'null');
      if (prev && prev.t > MIN_MARK && Date.now() - prev.at < MAX_AGE) mark = prev.t;
    } catch (e) {}

    /* build the waveform bars once (deterministic, no audio analysis) */
    root.querySelectorAll('.ar-audio__wave').forEach(function (w) {
      if (w.children.length) return;
      var h = '';
      for (var i = 0; i < BARS; i++) {
        var v = 20 + Math.round(Math.abs(Math.sin(i * 1.7)) * 66 + (i % 3) * 7);
        h += '<span style="height:' + Math.min(100, v) + '%"></span>';
      }
      w.innerHTML = h;
    });

    function dur() {
      var d = audio.duration;
      if (isFinite(d) && d > 0) return d;
      return parseFloat(root.dataset.arDuration || '0') || 0;
    }

    function paint() {
      var d = dur(), t = at(), pct = d ? Math.min(100, t / d * 100) : 0;
      root.style.setProperty('--ar-audio-p', pct.toFixed(2) + '%');
      if (time) time.textContent = fmt(t) + ' / ' + (d ? fmt(d) : '--:--');
      if (seek) {
        seek.setAttribute('aria-valuenow', Math.round(t));
        seek.setAttribute('aria-valuemax', Math.round(d));
        seek.setAttribute('aria-valuetext', fmt(t) + ' of ' + fmt(d));
      }
    }

    function fail(msg) {
      root.classList.add('has-error');
      root.classList.remove('is-playing', 'is-loading');
      if (err) err.textContent = msg || 'Audio unavailable right now. Please read on below.';
    }

    /* ---- media session: lock screen, car, headset ---- */
    function session() {
      if (!('mediaSession' in navigator)) return;
      try {
        var art = root.dataset.arArtwork;
        navigator.mediaSession.metadata = new window.MediaMetadata({
          title: root.dataset.arTitle || (root.querySelector('.ar-audio__title') || {}).textContent || 'Article audio',
          artist: root.dataset.arArtist || 'Lab Effects',
          album: root.dataset.arAlbum || 'The Science of Mapping Terpenes',
          artwork: art ? [{ src: art, sizes: '512x512' }] : []
        });
        var set = navigator.mediaSession.setActionHandler.bind(navigator.mediaSession);
        set('play', toggle); set('pause', toggle);
        set('seekbackward', function (d) { nudge(-(d && d.seekOffset || BACK_BY)); });
        set('seekforward', function (d) { nudge(d && d.seekOffset || FWD_BY); });
        set('previoustrack', function () { nudge(-BACK_BY); });
        set('nexttrack', function () { nudge(FWD_BY); });
        set('seekto', function (d) { if (d && d.seekTime != null) { audio.currentTime = d.seekTime; remember(d.seekTime); paint(); } });
        set('stop', function () { audio.pause(); audio.currentTime = 0; forget(); paint(); });
      } catch (e) { /* MediaMetadata unsupported: harmless */ }
    }
    function position() {
      if (!('mediaSession' in navigator) || !navigator.mediaSession.setPositionState) return;
      var d = dur(); if (!d) return;
      try {
        navigator.mediaSession.setPositionState({
          duration: d, playbackRate: audio.playbackRate,
          position: Math.min(at(), d)
        });
      } catch (e) {}
    }

    function nudge(by) {
      var d = dur();
      var t = Math.max(0, Math.min(d || Infinity, at() + by));
      audio.currentTime = t;
      remember(t);
      paint(); position();
    }

    function toggle() {
      if (root.classList.contains('has-error')) return;
      if (audio.paused) {
        if (!loaded) root.classList.add('is-loading');
        var pr = audio.play();
        if (pr && pr.catch) pr.catch(function () { fail(); });
      } else {
        audio.pause();
      }
    }

    play.addEventListener('click', toggle);
    if (back) back.addEventListener('click', function () { nudge(-BACK_BY); });
    if (fwd) fwd.addEventListener('click', function () { nudge(FWD_BY); });

    if (rate) rate.addEventListener('click', function () {
      rateIdx = (rateIdx + 1) % RATES.length;
      audio.playbackRate = RATES[rateIdx];
      rate.textContent = RATES[rateIdx] + '\u00d7';
      rate.setAttribute('aria-label', 'Playback speed ' + RATES[rateIdx] + ' times, tap to change');
      position();
    });

    /* ---- seeking: pointer drag + keyboard ---- */
    function seekTo(clientX) {
      var d = dur(); if (!d) return;
      var r = seek.getBoundingClientRect();
      var t = Math.max(0, Math.min(d, (clientX - r.left) / r.width * d));
      try { audio.currentTime = t; } catch (e) {}
      /* Set the mark from the target, not from the element: on a player that
         has not loaded yet the assignment above does nothing, and restore()
         would otherwise drag the listener back to where they were. */
      lastWrite = 0; remember(t);
      paint();
    }
    if (seek) {
      seek.addEventListener('pointerdown', function (e) {
        if (!dur()) { audio.load(); }
        dragging = true; seek.setPointerCapture(e.pointerId); seekTo(e.clientX);
      });
      seek.addEventListener('pointermove', function (e) { if (dragging) seekTo(e.clientX); });
      seek.addEventListener('pointerup', function () { dragging = false; position(); });
      seek.addEventListener('pointercancel', function () { dragging = false; });
      seek.addEventListener('keydown', function (e) {
        var k = e.key, d = dur();
        if (k === 'ArrowRight') nudge(5);
        else if (k === 'ArrowLeft') nudge(-5);
        else if (k === 'PageUp') nudge(FWD_BY);
        else if (k === 'PageDown') nudge(-BACK_BY);
        else if (k === 'Home') { audio.currentTime = 0; forget(); paint(); }
        else if (k === 'End' && d) { audio.currentTime = d - .25; remember(d - .25); paint(); }
        else if (k === ' ' || k === 'Enter') toggle();
        else return;
        e.preventDefault();
      });
    }

    /* ---- audio element state -> block classes ---- */
    audio.addEventListener('loadedmetadata', function () { loaded = true; root.classList.remove('is-loading'); restore(); paint(); position(); });
    audio.addEventListener('waiting', function () { root.classList.add('is-loading'); });
    audio.addEventListener('playing', function () { root.classList.remove('is-loading'); root.classList.add('is-playing'); session(); position(); });
    audio.addEventListener('play', function () { root.classList.add('is-playing'); });
    audio.addEventListener('pause', function () {
      root.classList.remove('is-playing');
      /* Write through on pause rather than waiting out the throttle: a pause
         is exactly the moment the tab may be closed or the buffer dropped. */
      lastWrite = 0; remember(audio.currentTime);
    });
    /* The browser dropped the media. currentTime is 0 now and the next play
       would start from the top, so put the mark back as soon as it reloads. */
    audio.addEventListener('emptied', restore);
    audio.addEventListener('loadstart', restore);
    audio.addEventListener('canplay', function () { loaded = true; root.classList.remove('is-loading'); });
    audio.addEventListener('timeupdate', function () { if (!dragging) { remember(audio.currentTime); paint(); } });
    audio.addEventListener('ratechange', position);
    audio.addEventListener('ended', function () { root.classList.remove('is-playing'); audio.currentTime = 0; forget(); paint(); });
    audio.addEventListener('error', function () { fail(); });
    audio.addEventListener('stalled', function () { root.classList.add('is-loading'); });

    window.addEventListener('pagehide', function () { lastWrite = 0; remember(audio.currentTime || mark); });
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'hidden') { lastWrite = 0; remember(audio.currentTime || mark); }
    });

    paint();
  }

  /* One voice at a time. A strain post carries two players (the narration and
     the strain's Strain Jam, snippets/lab-article-jam.liquid), and starting one
     pauses whatever else is playing so they never talk over each other. 'play'
     does not bubble, hence the capture listener. Guarded because this file can
     be included more than once on a page. */
  if (!window.__arAudioSolo) {
    window.__arAudioSolo = 1;
    document.addEventListener('play', function (e) {
      var t = e.target;
      if (!t || !t.classList || !t.classList.contains('ar-audio__native')) return;
      document.querySelectorAll('audio.ar-audio__native').forEach(function (a) {
        if (a !== t && !a.paused) a.pause();
      });
    }, true);
  }

  function boot() { document.querySelectorAll('.ar-audio').forEach(init); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', boot);
  window.LabArticleAudio = { init: init, boot: boot };
})();
